// Types for the new 130-item assessment system

export interface Assessment {
  id: string;
  name: string;
  email: string;
  role: string | null;
  practice_name: string | null;
  created_at: string;
  completed_at: string | null;
  question_order: string[];
  status: 'in_progress' | 'complete';
  current_question_index: number;
  token_id: string | null;
}

export interface AssessmentResponse {
  id: string;
  assessment_id: string;
  question_id: string;
  response_value: number;
  created_at: string;
}

export interface DomainScores {
  A?: number; // Patient equity/advocacy
  B?: number; // People leadership
  C?: number; // Operational grip
  D?: number; // Financial stewardship
  E?: number; // Governance/risk
  F?: number; // Strategy/innovation
  G?: number; // Influence/collaboration
  H?: number; // Personal resilience
  I?: number; // Sustainability strain
  J?: number; // Work-life balance
  K?: number; // Conflict engagement
  L?: number; // Leadership vs management
  M?: number; // Purpose vs profit
  N?: number; // Tenure fairness
  O?: number; // Autonomy expectations
  P?: number; // Values/wellbeing/consensus
}

export interface DerivedScores {
  sustainabilityStrain: number;
  workLifeStrain: number;
  conflictEngagement: number;
  decisionFollowThrough: number;
  consensusFriction: number;
  valuesClarity: number;
}

export interface MotivationSignature {
  autonomy: number;
  competence: number;
  relatedness: number;
  purpose: number;
  security: number;
  growth: number;
}

export interface Archetype {
  primary: string;
  secondary: string;
  modifiers: string[];
}

export interface ReportNarrative {
  contribution?: string;
  pressure?: string;
  values?: string;
  motivation?: string;
  sustainability?: string;
}

export interface ComputedResults {
  assessment_id: string;
  computed_at: string;
  domain_scores: DomainScores;
  derived_scores: DerivedScores;
  motivation_signature: MotivationSignature;
  archetype: Archetype;
  report_narrative: ReportNarrative;
}

// Domain labels for display
export const DOMAIN_LABELS: Record<string, string> = {
  A: 'Patient Equity & Advocacy',
  B: 'People Leadership',
  C: 'Operational Grip',
  D: 'Financial Stewardship',
  E: 'Governance & Risk',
  F: 'Strategy & Innovation',
  G: 'Influence & Collaboration',
  H: 'Personal Resilience',
  I: 'Sustainability',
  J: 'Work-Life Balance',
  K: 'Conflict Engagement',
  L: 'Leadership vs Management',
  M: 'Purpose vs Profit',
  N: 'Tenure Fairness',
  O: 'Autonomy',
  P: 'Values & Consensus',
};

// Contribution domains (B-G) for primary/secondary archetype
export const CONTRIBUTION_DOMAINS = ['B', 'C', 'D', 'E', 'F', 'G'];

// Core domains (A-H) for strengths/watch-outs analysis
export const CORE_DOMAINS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

// Motivation dimension labels
export const MOTIVATION_LABELS: Record<string, string> = {
  autonomy: 'Autonomy & Agency',
  competence: 'Competence & Effectiveness',
  relatedness: 'Relatedness & Belonging',
  purpose: 'Purpose & Service',
  security: 'Security & Financial Calm',
  growth: 'Growth & Exploration',
};

// Score band helpers
export type ScoreBand = 'low' | 'mid' | 'high';

export function getScoreBand(score: number): ScoreBand {
  if (score < 45) return 'low';
  if (score < 65) return 'mid';
  return 'high';
}

export function getScoreBandLabel(band: ScoreBand): string {
  switch (band) {
    case 'low': return 'Development Area';
    case 'mid': return 'Balanced';
    case 'high': return 'Strength';
  }
}

// Likert scale options (unchanged from original)
export const LIKERT_OPTIONS = [
  { value: 1, label: 'Strongly disagree', color: 'bg-red-500' },
  { value: 2, label: 'Disagree', color: 'bg-orange-500' },
  { value: 3, label: 'Slightly disagree', color: 'bg-amber-500' },
  { value: 4, label: 'Neutral', color: 'bg-gray-400' },
  { value: 5, label: 'Slightly agree', color: 'bg-lime-500' },
  { value: 6, label: 'Agree', color: 'bg-green-500' },
  { value: 7, label: 'Strongly agree', color: 'bg-emerald-600' },
];
